import { fetchAuthSession } from 'aws-amplify/auth';
import { AppSpec, GeneratedFile } from '../types';

/**
 * Central API client for EA-SpecDriven.
 *
 * Two backends sit behind the same nginx host:
 *   - ea-backend (port 3010)        →  {API_BASE}/spec-driven/*   (optional auth)
 *   - ea-provisioning (port 3007)   →  {PROV_BASE}/api/prov/*     (REQUIRES Cognito JWT)
 *
 * Every request attaches the Cognito ID token when a session exists —
 * without it the provisioning agent rejects with 401.
 */

export const API_BASE = (
  process.env.REACT_APP_API_URL || 'https://saeg.japaneast.cloudapp.azure.com/ea-api/api'
).replace(/\/$/, '');

// strip trailing /api → /ea-api, then re-append /api/prov
export const PROV_BASE = API_BASE.replace(/\/api\/?$/, '') + '/api/prov';

export async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      message = body.error || body.message || message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<T>;
}

/* ── Spec-Driven backend (ea-backend) ─────────────────────────── */

export function listApps(): Promise<AppSpec[]> {
  return request<AppSpec[]>(`${API_BASE}/spec-driven/apps`);
}

export function getApp(id: string): Promise<AppSpec> {
  return request<AppSpec>(`${API_BASE}/spec-driven/apps/${encodeURIComponent(id)}`);
}

export function saveApp(spec: AppSpec): Promise<AppSpec> {
  return request<AppSpec>(`${API_BASE}/spec-driven/apps`, {
    method: 'POST',
    body: JSON.stringify(spec),
  });
}

export function deleteApp(id: string): Promise<{ success?: boolean }> {
  return request(`${API_BASE}/spec-driven/apps/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function downloadAppFiles(id: string): Promise<{ files: GeneratedFile[] }> {
  return request(`${API_BASE}/spec-driven/download/${encodeURIComponent(id)}`);
}

export function fullDeploy(specId: string): Promise<any> {
  return request(`${API_BASE}/spec-driven/full-deploy`, {
    method: 'POST',
    body: JSON.stringify({ specId }),
  });
}

/* ── Provisioning Agent (ea-provisioning, JWT required) ───────── */

export interface WizardStepResult {
  step: string;
  status: 'success' | 'failed' | 'skipped';
  error?: string;
  result?: any;
}

export interface WizardResult {
  success: boolean;
  summary?: string;
  appName?: string;
  url?: string;
  steps?: WizardStepResult[];
}

export interface WizardPayload {
  appName: string;
  description: string;
  requirements: string;
  skills: string[];
  llmModel?: string;
  backendServer?: string;
  options: Record<string, any>;
}

export function runWizard(payload: WizardPayload): Promise<WizardResult> {
  return request<WizardResult>(`${PROV_BASE}/wizard`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export interface InfraServer {
  server: string;
  ip: string;
  status: 'online' | 'unreachable' | string;
  pm2Processes?: any[];
}

export interface InfraStatus {
  servers?: InfraServer[];
  ports?: { port: number; service?: string; appId?: string }[];
}

export function getInfraStatus(): Promise<InfraStatus> {
  return request<InfraStatus>(`${PROV_BASE}/infra/status`);
}

export function getProvApps(): Promise<any[]> {
  return request<any[]>(`${PROV_BASE}/apps`);
}

/* ── Standalone Deploy (ea-backend, admin group required) ─────── */

const STANDALONE_BASE = `${API_BASE}/spec-driven/standalone`;

/* ── Auth mode (server-side setting; toggled from the Settings UI) ─────── */

// PUBLIC read — no token. Tells the app whether Cognito login is required or
// disabled (guest-admin mode). Falls back to "login required" if unreachable.
export async function getAuthMode(): Promise<{ loginDisabled: boolean }> {
  try {
    const res = await fetch(`${STANDALONE_BASE}/auth-mode`);
    if (!res.ok) return { loginDisabled: false };
    return await res.json();
  } catch {
    return { loginDisabled: false };
  }
}

// Admin-gated write. In guest-admin mode any visitor is admin and can re-enable
// login; in login-enabled mode only a real Cognito admin can disable it.
export async function setAuthMode(loginDisabled: boolean): Promise<{ loginDisabled: boolean }> {
  return request(`${STANDALONE_BASE}/auth-mode`, {
    method: 'PUT',
    body: JSON.stringify({ loginDisabled }),
  });
}

export interface StandaloneApp {
  appId: string;
  name: string;
  description: string;
  icon: string;
  enabled: boolean;
  hasAI: boolean;
  version: string;
  publishedAt: string;
}

export interface StandaloneAiConfig {
  whisper_url: string;
  whisper_model: string;
  ollama_url: string;
  ollama_model: string;
  tts_url: string;
  lfm_audio_url: string;
}

export interface StandaloneSpec {
  app: string;
  registry: string;
  controlPlaneUrl: string;
  httpsPort: number;
  httpPort: number;
  updateMode: 'notify' | 'auto' | 'manual';
  sessionHours: number;
  adminEmail: string;
  allowedIps: string[];
  engine?: 'docker' | 'podman'; // container engine baked into the installer (YAKUGO_ENGINE)
  domain?: string; // optional subdomain under sae-g.com → trusted HTTPS cert
  ai?: StandaloneAiConfig;
}

export interface StandalonePackageResult {
  buildId: string;
  keyId: string;
  version: string;
  bundleName: string;
  downloadUrl: string;
  downloadExpires: string;
  instructions: { linux: string; windows: string };
  adminUser: string;
  adminPassword: string;
}

export interface StandaloneDeployStep {
  name: string;
  status: string;
  detail?: string;
}

export interface StandaloneDeployResult {
  success: boolean;
  steps: StandaloneDeployStep[];
  version?: string;
  keyId?: string;
  url?: string;
  error?: string;
  firstInstall?: boolean;
  adminUser?: string | null;
  adminPassword?: string | null;
  note?: string;
}

export interface Deployment {
  id: string;
  app: string;
  target: string;
  status: 'deployed' | 'issued';
  method: 'push' | 'download';
  host?: string;
  domain?: string;
  url?: string;
  version: string;
  keyId: string;
  adminEmail: string;
  deployedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StandaloneProfile {
  id: string;
  name: string;
  host: string;
  port: number;
  username: string;
  hasKey: boolean;
  hasPassword?: boolean;
  spec: StandaloneSpec;
  createdAt: string;
  updatedAt: string;
}

export interface StandaloneRequest {
  id: string | number;
  user_id: string;
  email: string;
  purpose: string;
  target_os: string;
  status: string;
  deploy_key_id: string | null;
  created_at: string;
  decided_at: string | null;
  decided_by: string | null;
}

export interface StandaloneKey {
  id: string;
  appId: string;
  label: string;
  requesterEmail: string;
  requestId: string | number | null;
  revoked: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  version: string;
  revokedAt?: string;
}

/* ── Hub list ─────────────────────────────────────────────────── */

export function getStandaloneApps(): Promise<{ apps: StandaloneApp[] }> {
  return request<{ apps: StandaloneApp[] }>(`${STANDALONE_BASE}/apps`);
}

/* ── Per-app spec / package / deploy ──────────────────────────── */

export function getStandaloneSpec(appId: string): Promise<StandaloneSpec> {
  return request<StandaloneSpec>(`${STANDALONE_BASE}/${encodeURIComponent(appId)}/spec`);
}

/** Prebuilt OFFLINE / air-gapped package (all images baked in, installs with no
 *  internet). Returns a presigned download link, or 404 if none is published. */
export interface StandaloneOffline {
  downloadUrl: string;
  filename: string;
  version: string | null;
  sizeMb: number | null;
  engine: string | null;
  adminEmail: string | null;
}
export function getStandaloneOffline(appId: string): Promise<StandaloneOffline> {
  return request<StandaloneOffline>(`${STANDALONE_BASE}/${encodeURIComponent(appId)}/offline`);
}

export function packageStandalone(
  appId: string,
  spec: StandaloneSpec,
  label?: string,
  requesterEmail?: string
): Promise<StandalonePackageResult> {
  return request<StandalonePackageResult>(
    `${STANDALONE_BASE}/${encodeURIComponent(appId)}/package`,
    {
      method: 'POST',
      body: JSON.stringify({ ...spec, label, requesterEmail }),
    }
  );
}

export interface StandaloneDeployPayload {
  host?: string;
  port?: number;
  username?: string;
  privateKey?: string;
  // Synology / password-auth mode: authenticate with a password instead of a
  // PEM key, and build images on the target with sudo (no registry pull).
  password?: string;
  authMode?: 'key' | 'password';
  buildOnHost?: boolean;
  profileId?: string;
  spec?: StandaloneSpec;
  label?: string;
  wipeData?: boolean;
  // Install faster-whisper (STT) locally on the target during the deploy;
  // the backend then points the app's Whisper URL at the local install.
  installWhisper?: boolean;
}

/**
 * Push-deploy over SSH. Long-running (up to ~10 minutes) — uses its own
 * fetch with an AbortController timeout, and keeps the `steps` array even
 * when the backend answers with a non-2xx failure body.
 */
export async function deployStandalone(
  appId: string,
  payload: StandaloneDeployPayload
): Promise<StandaloneDeployResult> {
  const token = await getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10 * 60 * 1000); // 10 min
  try {
    const res = await fetch(`${STANDALONE_BASE}/${encodeURIComponent(appId)}/deploy`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
    let body: any = null;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    if (body && Array.isArray(body.steps)) {
      return body as StandaloneDeployResult;
    }
    if (!res.ok) {
      throw new ApiError(
        res.status,
        (body && (body.error || body.message)) || `Deploy failed (${res.status})`
      );
    }
    return body as StandaloneDeployResult;
  } finally {
    clearTimeout(timer);
  }
}

/* ── Saved deploy profiles (per-app) ──────────────────────────── */

export function listStandaloneProfiles(
  appId: string
): Promise<{ profiles: StandaloneProfile[] }> {
  return request<{ profiles: StandaloneProfile[] }>(
    `${STANDALONE_BASE}/${encodeURIComponent(appId)}/profiles`
  );
}

export interface StandaloneProfilePayload {
  id?: string;
  name: string;
  host: string;
  port?: number;
  username?: string;
  privateKey?: string;
  password?: string;
  authMode?: 'key' | 'password';
  buildOnHost?: boolean;
  spec: StandaloneSpec;
}

export function saveStandaloneProfile(
  appId: string,
  payload: StandaloneProfilePayload
): Promise<{ profile: StandaloneProfile }> {
  return request<{ profile: StandaloneProfile }>(
    `${STANDALONE_BASE}/${encodeURIComponent(appId)}/profiles`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

export function deleteStandaloneProfile(
  appId: string,
  profileId: string
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(
    `${STANDALONE_BASE}/${encodeURIComponent(appId)}/profiles/${encodeURIComponent(profileId)}`,
    { method: 'DELETE' }
  );
}

/* ── Cross-app requests & keys ────────────────────────────────── */

export function listStandaloneRequests(
  all = false
): Promise<{ requests: StandaloneRequest[] }> {
  return request<{ requests: StandaloneRequest[] }>(
    `${STANDALONE_BASE}/requests${all ? '?all=1' : ''}`
  );
}

export interface StandaloneApprovePayload {
  appId: string;
  spec?: StandaloneSpec;
}

export function approveStandaloneRequest(
  id: string | number,
  payload: StandaloneApprovePayload
): Promise<{
  success: boolean;
  downloadUrl: string;
  version: string;
  keyId: string;
  requesterEmail: string;
}> {
  return request(`${STANDALONE_BASE}/requests/${encodeURIComponent(String(id))}/approve`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function rejectStandaloneRequest(
  id: string | number,
  reason?: string
): Promise<{ success?: boolean }> {
  return request(`${STANDALONE_BASE}/requests/${encodeURIComponent(String(id))}/reject`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export function listStandaloneKeys(app?: string): Promise<{ keys: StandaloneKey[] }> {
  return request<{ keys: StandaloneKey[] }>(
    `${STANDALONE_BASE}/keys${app ? `?app=${encodeURIComponent(app)}` : ''}`
  );
}

export function revokeStandaloneKey(id: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(
    `${STANDALONE_BASE}/keys/${encodeURIComponent(id)}/revoke`,
    { method: 'POST' }
  );
}

/* ── Deployments (cross-app + per-app) ────────────────────────── */

export function listAllDeployments(): Promise<{ deployments: Deployment[] }> {
  return request<{ deployments: Deployment[] }>(`${STANDALONE_BASE}/deployments`);
}

export function listAppDeployments(
  appId: string
): Promise<{ deployments: Deployment[] }> {
  return request<{ deployments: Deployment[] }>(
    `${STANDALONE_BASE}/${encodeURIComponent(appId)}/deployments`
  );
}

export interface DeleteDeploymentResult {
  success: boolean;
  teardown?: { attempted: boolean; ok: boolean; detail: string };
}

export function deleteDeployment(
  appId: string,
  id: string
): Promise<DeleteDeploymentResult> {
  return request<DeleteDeploymentResult>(
    `${STANDALONE_BASE}/${encodeURIComponent(appId)}/deployments/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  );
}

export interface ResetPasswordResult {
  success: boolean;
  adminUser: string;
  adminPassword: string;
  note: string;
}

export function resetDeploymentPassword(
  appId: string,
  id: string
): Promise<ResetPasswordResult> {
  return request<ResetPasswordResult>(
    `${STANDALONE_BASE}/${encodeURIComponent(appId)}/deployments/${encodeURIComponent(id)}/reset-password`,
    { method: 'POST' }
  );
}
